import { useForm } from "react-hook-form";
import CustomInput from "../../../../../components/CustomInput/CustomInput";
import { PersonalDetailsFields } from "./personalDetails.types";
import LoadingAnimation from "../../../../../assets/svg/LoadingAnimation";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { updateCandidate } from "endpoints/candidate.service";
import { useSnackbar } from "notistack";
import { updateUserProfile } from "@endpoints/auth.service";

const PersonalDetails: React.FC = () => {
  const { user } = useSelector((state: any) => state.auth);
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<PersonalDetailsFields>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNo: user.phoneNo ?? "N/A",
      isEditing: false,
      isLoading: false,
    },
  });

  const fields = watch();

  const onSubmit = useCallback(async (data) => {
    try {
      setValue("isLoading", true);
      const phoneNoToSend = data.phoneNo === "N/A" ? undefined : data.phoneNo;
      const { success } = await updateUserProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNo: phoneNoToSend,
      });

      if (success) {
        setValue("firstName", data.firstName);
        setValue("lastName", data.lastName);
        setValue("email", data.email);
        setValue("phoneNo", phoneNoToSend);

        enqueueSnackbar("Personal details updated successfully", {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to update personal details", {
        variant: "error",
      });
    } finally {
      setValue("isLoading", false);
      setValue("isEditing", false);
    }
  }, []);

  const toggleEdit = () => {
    if (fields?.isEditing) {
      handleSubmit(onSubmit)();
    } else {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNo: user.phoneNo ?? "N/A",
        isEditing: !fields?.isEditing,
        isLoading: false,
      });
    }
  };

  return (
    <div className="flex-1 bg-white p-6 rounded-md border border-gray-200 text-xs h-fit">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xs font-semibold">Personal Details</h2>
        <button
          onClick={toggleEdit}
          className="text-xs text-primary"
          disabled={fields?.isLoading}
        >
          {fields?.isLoading ? (
            <LoadingAnimation color={"black"} />
          ) : fields?.isEditing ? (
            "Save"
          ) : (
            "Edit"
          )}
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="mb-4">
          {/* @ts-ignore */}
          <CustomInput
            label="First name"
            name="firstName"
            control={control}
            errors={errors}
            disabled={!fields?.isEditing || fields?.isLoading}
            rules={
              fields?.isEditing && {
                required: "This field is required.",
              }
            }
          />
        </div>

        <div className="mb-4">
          {/* @ts-ignore */}
          <CustomInput
            label="Last name"
            name="lastName"
            control={control}
            errors={errors}
            disabled={!fields?.isEditing || fields?.isLoading}
            rules={
              fields?.isEditing && {
                required: "This field is required.",
              }
            }
          />
        </div>

        <div className="mb-4">
          {/* @ts-ignore */}
          <CustomInput
            label="Email Address"
            name="email"
            control={control}
            errors={errors}
            disabled={true}
          />
        </div>

        <div className="mb-4">
          {/* @ts-ignore */}
          <CustomInput
            label="Contact Number"
            name="phoneNo"
            control={control}
            errors={errors}
            disabled={!fields?.isEditing || fields?.isLoading}
            rules={
              fields?.isEditing && {
                required: "This field is required.",
              }
            }
          />
        </div>
      </form>
    </div>
  );
};

export default PersonalDetails;
